import React from "react";
import {
  useViewport,
  NewRadioGroup,
  OverlayProvider,
  NewSelect,
} from "@bca/ui-library";
import remarkGfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  StyledAddContactPerson,
  StyledContent,
  StyledTitle,
  StyledSection,
  StyledInterestsGroup,
  StyledRadioGroup,
  StyledRadioLabel,
  StyledRequired,
  StyledInterestsLabel,
  StyledInterestsButtonsGroup,
  StyledInterestButton,
  StyledOtherInformGroup,
  StyledOtherInformLabel,
  StyledCheckboxGroup,
  StyledCheckbox,
  StyledUploadDocumentsGroup,
  StyledUploadDocumentsTtile,
  StyledUploadDocumentsParagraph,
  StyledUploadDocumentsLabel,
  StyledUploadButton,
  StyledTCGroup,
  StyledTCCheckbox,
  StyledTCLabel,
  StyledInput,
  StyledInputGroup,
  StyledNewSelect,
  StyledUploadGroup,
  StyledUploadButtonContainer,
  StyledUploadButtonsGroup,
  StyledUploadDocumentsSpecificText,
  StyledUploadDocumentsInfo,
  StyledUploadDocumentsInfoText,
  StyledUploadDocumentsSpecificCheckboxContainer,
  StyledSpecificForCountryRadioGroup,
  StyledSpecificForCountryRadioLabel,
  StyledRadioButtonGroup,
  StyledStateSelect,
} from "./AddContactPerson.styled";
import Button from "../../components/Button/Button";
import { getAddContactPerson } from "../../reducers/addContactPersonSlice";
import {
  changeCompanyName,
  changeStreetNameAndNumber,
  changeCity,
  changeZipCode,
  changeSalutation,
  changeFirstName,
  changeMiddleName,
  changeLastName,
  changePhoneNumber,
  changeEmail,
  changeCountryInterestsCheckbox,
  changeInformedByEmailCheckbox,
  changeInformedByOtherWaysCheckbox,
  changeUploadDocuments,
  changeTcAndPrivacy,
  changeSpecificCheckbox,
  resetCompanyInformation,
  changeStates,
} from "../../reducers/formSlice";
import { nameValidation } from "../../constants/nameValidation";
import { changeShowLoadingScreenState } from "../../reducers/loadingScreenSlice";
import {
  setPageError,
  setShowServerErrorMessage,
} from "../../reducers/errorManagementSlice";
import {
  ADD_CONTACT_PERSON_PATH,
  COMPANY_INFORMATION_PATH,
  HOME_PATH,
} from "../../constants/paths";
import { setSfLeadId } from "../../reducers/salesforceLeadIdSlice";
import {
  initializeDigitalData,
  updateInitializedDigitalDataForFirstPage,
} from "../../hooks/scriptsHooks";

const AddContactPerson = () => {
  const { page_content, loading } = useSelector(
    (state) => state.addContactPersonScreen
  );

  const { country_name: countrySelected, ...formState } = useSelector(
    (state) => state.form
  );
  const navigate = useNavigate();

  if (!countrySelected) {
    navigate(HOME_PATH, { replace: true });
  }

  const { value: languageCode, country_iso3 } = useSelector(
    (state) => state.languageOptions
  );

  const dispatch = useDispatch();

  const submitForm = async () => {
    const baseUrl = process.env.REACT_APP_API;

    dispatch(changeShowLoadingScreenState(true));

    const data = await fetch(
      `${baseUrl}/api/contact-person-submit/${formState.company_id}/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          accept: "application/json",
          "Accept-Language": languageCode === "en" ? "en-UK" : languageCode,
        },
        body: JSON.stringify({ ...formState, country_iso3 }),
      }
    );

    if (data.status === 400) {
      dispatch(setPageError({ page: "submit_registration", status: 400 }));
    }

    dispatch(setShowServerErrorMessage(data.status));
    if (data.ok) {
      const response = await data.json();
      dispatch(setSfLeadId(response?.sf_lead_id));
      dispatch(changeShowLoadingScreenState(false));
      navigate("/thank-you");
    }
  };

  const [continueButtonDisabled, setContinueButtonDisabled] =
    React.useState(true);

  const [firstNameVerified, setFirstNameVerified] = React.useState(true);

  const [middleNameVerified, setMiddleNameVerified] = React.useState(true);

  const [lastNameVerified, setLastNameVerified] = React.useState(true);

  const [phoneNumberVerified, setPhoneNumberVerified] = React.useState(true);

  const [emailVerified, setEmailVerified] = React.useState(true);

  const refs = React.useRef([]);

  let docValue = 0;

  const acceptedFilesType = page_content?.supported_file_extensions?.extensions;

  const fileCount = page_content?.documents_list?.map((document) => {
    const newArray = [...Array(document.total_amount)].map(() => {
      const newValue = docValue;
      docValue += 1;

      return newValue;
    });

    return newArray;
  });

  const vp = useViewport();
  const isMobile = vp.lessThan.md;

  const handleClick = (ref, isPicked, index = 0) => {
    if (isPicked) {
      dispatch(
        changeUploadDocuments({
          index,
          data: {
            isPicked: false,
            file: null,
            filename: null,
            document_type: null,
          },
        })
      );
    } else {
      ref.click();
    }
  };

  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleChange = async (event, document, index = 0) => {
    const fileUploaded = event.target.files[0];

    const documentsSupportedList = acceptedFilesType.map((file) =>
      file.replace(".", "")
    );

    if (documentsSupportedList.includes(fileUploaded.type.split("/")[1])) {
      const base64 = await convertBase64(fileUploaded);

      dispatch(
        changeUploadDocuments({
          index,
          data: {
            isPicked: true,
            file: base64,
            filename: fileUploaded.name,
            document_type: document?.document_type,
          },
        })
      );
    }
  };

  React.useEffect(() => {
    if (window.digitalData) {
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "1.2",
          stepName: "add contact person",
        },
        pageInfo: {
          pageTitle: "add contact person",
          pagePath: `/${ADD_CONTACT_PERSON_PATH}`,
          previousPagePath: `/${COMPANY_INFORMATION_PATH}`,
        },
      });
    } else {
      initializeDigitalData();
      updateInitializedDigitalDataForFirstPage();
      window.digitalData.push({
        event: {
          name: "pageview",
        },
        journey: {
          name: "new-registration",
          stepNumber: "1.2",
          stepName: "add contact person",
        },
        pageInfo: {
          pageTitle: "add contact person",
          pagePath: `/${ADD_CONTACT_PERSON_PATH}`,
          previousPagePath: `/${COMPANY_INFORMATION_PATH}`,
        },
        site: {
          brandCode: country_iso3,
          brandName: "bca europe",
          language: languageCode,
          platformType: "cms",
          country: languageCode,
        },
      });
    }
  }, [country_iso3, languageCode]);

  React.useEffect(() => {
    dispatch(
      getAddContactPerson(
        countrySelected,
        languageCode,
        formState?.company_type?.value
      )
    );
  }, [dispatch, countrySelected, languageCode, formState?.company_type?.value]);

  React.useEffect(() => {
    const documentsVerify = () => {
      const verify = page_content?.documents_list?.map((document, index) => {
        return [...Array(document.total_amount)].map((_, i) => {
          return document.required
            ? formState?.uploaded_documents.value[fileCount[index][i]]
                ?.isPicked || false
            : true;
        });
      });

      const verifyArray = page_content?.documents_list?.map(
        (document, index) => {
          let isVerified = true;
          if (document.required) {
            isVerified =
              verify[index].filter((item) => item === true).length >=
              document.required_amount;
          }

          return isVerified;
        }
      );

      return verifyArray?.every((item) => item === true);
    };

    if (
      (page_content?.company_name_form?.required === true
        ? formState.company_name?.value !== ""
        : true) &&
      (page_content?.street_name_and_number_form?.required === true
        ? formState.street?.value !== ""
        : true) &&
      (page_content?.city_form?.required === true
        ? formState.city?.value !== ""
        : true) &&
      (page_content?.zip_code_form?.required === true
        ? formState.zipCode?.value !== ""
        : true) &&
      (page_content?.salutation?.required === true
        ? formState.salutation?.value !== ""
        : true) &&
      (page_content?.first_name?.required === true
        ? formState.first_name?.value !== ""
        : true) &&
      (page_content?.middle_name?.required === true
        ? formState.middle_name?.value !== ""
        : true) &&
      (page_content?.last_name?.required === true
        ? formState.last_name?.value !== ""
        : true) &&
      (page_content?.phone_number?.required === true
        ? formState.phone_number?.value !== ""
        : true) &&
      (page_content?.email?.required === true
        ? formState.email?.value !== ""
        : true) &&
      (page_content?.country_interests_checkbox?.required === true
        ? formState?.country_interests_checkbox?.value?.some(
            (interest) => interest.checked === true
          )
        : true) &&
      (page_content?.informed_by_email_checkbox?.required === true
        ? !!formState?.informed_by_email_checkbox?.value
        : true) &&
      (page_content?.informed_by_other_ways_checkbox?.required === true
        ? formState?.informed_by_other_ways_checkbox?.value?.some(
            (interest) => interest.checked === true
          )
        : true) &&
      (page_content?.tc_and_privacy?.required === true
        ? formState.accept_conditions?.value
        : true) &&
      firstNameVerified &&
      middleNameVerified &&
      lastNameVerified &&
      phoneNumberVerified &&
      emailVerified &&
      documentsVerify()
    ) {
      setContinueButtonDisabled(false);
    } else {
      setContinueButtonDisabled(true);
    }
  }, [
    emailVerified,
    fileCount,
    firstNameVerified,
    formState.accept_conditions?.value,
    formState.city?.value,
    formState.company_name?.value,
    formState?.country_interests_checkbox?.value,
    formState.email?.value,
    formState.first_name?.value,
    formState?.informed_by_email_checkbox?.value,
    formState?.informed_by_other_ways_checkbox?.value,
    formState.last_name?.value,
    formState.middle_name?.value,
    formState.phone_number?.value,
    formState.salutation?.value,
    formState.street?.value,
    formState?.uploaded_documents.value,
    formState.zipCode?.value,
    lastNameVerified,
    middleNameVerified,
    page_content?.chamber_of_commerce_number,
    page_content?.city_form?.required,
    page_content?.company_name_form?.required,
    page_content?.country_interests_checkbox?.required,
    page_content?.documents_list,
    page_content?.email?.required,
    page_content?.first_name?.required,
    page_content?.informed_by_email_checkbox?.required,
    page_content?.informed_by_other_ways_checkbox?.required,
    page_content?.last_name?.required,
    page_content?.middle_name?.required,
    page_content?.phone_number?.required,
    page_content?.salutation?.required,
    page_content?.street_name_and_number_form?.required,
    page_content?.tc_and_privacy?.required,
    page_content?.zip_code_form?.required,
    phoneNumberVerified,
  ]);

  const markdown = `<div>
  
  ${page_content?.tc_and_privacy?.label}

  </div>`;

  const handleGoBack = () => {
    dispatch(resetCompanyInformation());
    navigate(-1);
  };

  const maximumFileSize = page_content?.info_maximum_file_size?.split(/\s{2,}/);

  return (
    <OverlayProvider>
      {!loading && (
        <StyledAddContactPerson>
          <StyledContent>
            <StyledTitle variant="h1" isMobile={isMobile}>
              {page_content?.header}
            </StyledTitle>
            <StyledSection isMobile={isMobile}>
              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-company-name"
                  state="default"
                  colorOn="bg_white"
                  label={page_content?.company_name_form?.label}
                  required={page_content?.company_name_form?.required}
                  value={formState.company_name.value}
                  hasSelect={false}
                  onChange={(e) => dispatch(changeCompanyName(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-street-name-and-number"
                  state="default"
                  colorOn="bg_white"
                  label={page_content?.street_name_and_number_form?.label}
                  required={page_content?.street_name_and_number_form?.required}
                  value={formState.street.value}
                  hasSelect={false}
                  onChange={(e) =>
                    dispatch(changeStreetNameAndNumber(e.target.value))
                  }
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-city"
                  state="default"
                  colorOn="bg_white"
                  label={page_content?.city_form?.label}
                  required={page_content?.city_form?.required}
                  value={formState.city.value}
                  hasSelect={false}
                  onChange={(e) => dispatch(changeCity(e.target.value))}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-zip-code"
                  state="default"
                  colorOn="bg_white"
                  label={page_content?.zip_code_form?.label}
                  required={page_content?.zip_code_form?.required}
                  value={formState.zip_code.value}
                  hasSelect={false}
                  onChange={(e) => dispatch(changeZipCode(e.target.value))}
                />
              </StyledInputGroup>

              {page_content?.states && (
                <StyledStateSelect
                  data-cy="add-contact-states"
                  onChange={(event) =>
                    dispatch(changeStates(event.target.value))
                  }
                  label={page_content?.states?.label}
                  value={formState?.states?.value}
                >
                  {page_content?.states?.value?.map((state) => (
                    <NewSelect.Option key={state} value={state}>
                      {state}
                    </NewSelect.Option>
                  ))}
                </StyledStateSelect>
              )}

              <StyledInputGroup>
                <StyledNewSelect
                  data-cy="add-contact-salutation-select"
                  value={formState.salutation.value}
                  label={page_content?.salutation?.label}
                  onChange={(e) => dispatch(changeSalutation(e.target.value))}
                >
                  {page_content?.salutation?.options?.map((option) => {
                    return (
                      <NewSelect.Option key={option.value} value={option.value}>
                        {option.label}
                      </NewSelect.Option>
                    );
                  })}
                </StyledNewSelect>
                <StyledInput
                  data-cy="add-contact-first_name"
                  state={firstNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    firstNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.first_name?.label}
                  required={page_content?.first_name?.required}
                  value={formState.first_name.value}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);

                    dispatch(changeFirstName(e.target.value));

                    if (e.target.value !== "") {
                      setFirstNameVerified(regex.test(e.target.value));
                    } else {
                      setFirstNameVerified(false);
                    }
                  }}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-middle-name"
                  state={middleNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    middleNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.middle_name?.label}
                  required={page_content?.middle_name?.required}
                  value={formState.middle_name.value}
                  hasSelect={false}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);

                    dispatch(changeMiddleName(e.target.value));

                    if (e.target.value !== "") {
                      setMiddleNameVerified(regex.test(e.target.value));
                    } else {
                      setMiddleNameVerified(false);
                    }
                  }}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-last-name"
                  state={lastNameVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    lastNameVerified
                      ? ""
                      : page_content?.only_letters_error_messages_block
                  }
                  label={page_content?.last_name?.label}
                  required={page_content?.last_name?.required}
                  value={formState.last_name.value}
                  hasSelect={false}
                  onChange={(e) => {
                    const regex = new RegExp(nameValidation);

                    dispatch(changeLastName(e.target.value));

                    if (e.target.value !== "") {
                      setLastNameVerified(regex.test(e.target.value));
                    } else {
                      setLastNameVerified(false);
                    }
                  }}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-phone-number"
                  state={phoneNumberVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    phoneNumberVerified
                      ? ""
                      : page_content?.phone_number_error_message_block
                  }
                  label={page_content?.phone_number?.label}
                  required={page_content?.phone_number?.required}
                  value={formState.phone_number.value}
                  hasSelect={false}
                  onChange={(e) => {
                    let regex;

                    dispatch(changePhoneNumber(e.target.value));

                    if (page_content?.phone_number_validation_block) {
                      regex = new RegExp(
                        page_content?.phone_number_validation_block
                      );
                    }

                    if (e.target.value !== "") {
                      setPhoneNumberVerified(regex.test(e.target.value));
                    } else {
                      setPhoneNumberVerified(false);
                    }
                  }}
                />
              </StyledInputGroup>

              <StyledInputGroup>
                <StyledInput
                  data-cy="add-contact-email"
                  state={emailVerified ? "default" : "error"}
                  colorOn="bg_white"
                  message={
                    emailVerified ? "" : page_content?.email_error_message
                  }
                  label={page_content?.email?.label}
                  required={page_content?.email?.required}
                  value={formState.email.value}
                  hasSelect={false}
                  onChange={(e) => {
                    let regex;

                    dispatch(changeEmail(e.target.value));

                    if (page_content?.email_validation) {
                      regex = new RegExp(page_content?.email_validation);
                    }

                    if (e.target.value !== "") {
                      setEmailVerified(regex.test(e.target.value));
                    } else {
                      setEmailVerified(false);
                    }
                  }}
                />
              </StyledInputGroup>

              <StyledInterestsGroup>
                <StyledInterestsLabel>
                  {page_content?.country_interests_checkbox?.label}
                  {page_content?.country_interests_checkbox?.required && (
                    <StyledRequired>*</StyledRequired>
                  )}
                </StyledInterestsLabel>
                <StyledInterestsButtonsGroup isMobile={isMobile}>
                  {page_content?.country_interests_checkbox?.options.map(
                    (interest, index) => (
                      <StyledInterestButton
                        data-cy="add-contact-interest-button"
                        key={interest.label}
                        text={interest.label}
                        variant={
                          formState?.country_interests_checkbox?.value[index]
                            ?.checked
                            ? "primary"
                            : "secondary"
                        }
                        onClick={() =>
                          dispatch(
                            changeCountryInterestsCheckbox(interest.value)
                          )
                        }
                      />
                    )
                  )}
                </StyledInterestsButtonsGroup>
              </StyledInterestsGroup>

              <StyledRadioGroup isMobile={isMobile}>
                <StyledRadioLabel isMobile={isMobile}>
                  {page_content?.informed_by_email_checkbox?.label}
                  {page_content?.informed_by_email_checkbox?.required && (
                    <StyledRequired>*</StyledRequired>
                  )}
                </StyledRadioLabel>
                <StyledRadioButtonGroup
                  data-cy="add-contact-informed-by-email-checkbox"
                  onChange={(event) =>
                    dispatch(changeInformedByEmailCheckbox(event.target.value))
                  }
                  name="email-inform"
                  value={formState?.informed_by_email_checkbox.value}
                >
                  <NewRadioGroup.Button
                    data-cy="add-contact-informed-by-email-checkbox-yes"
                    value={
                      page_content?.informed_by_email_checkbox?.options[0]
                        ?.value
                    }
                    label={
                      page_content?.informed_by_email_checkbox?.options[0]
                        ?.label
                    }
                  />
                  <NewRadioGroup.Button
                    data-cy="add-contact-informed-by-email-checkbox-no"
                    value={
                      page_content?.informed_by_email_checkbox?.options[1].value
                    }
                    label={
                      page_content?.informed_by_email_checkbox?.options[1].label
                    }
                  />
                </StyledRadioButtonGroup>
              </StyledRadioGroup>

              <StyledOtherInformGroup isMobile={isMobile}>
                <StyledOtherInformLabel>
                  {page_content?.informed_by_other_ways_checkbox?.label}
                  {page_content?.informed_by_other_ways_checkbox?.required && (
                    <StyledRequired>*</StyledRequired>
                  )}
                </StyledOtherInformLabel>
                <StyledCheckboxGroup isMobile={isMobile}>
                  {formState?.informed_by_other_ways_checkbox?.value?.map(
                    (checkbox, index) => (
                      <StyledCheckbox
                        data-cy="add-contact-informed-by-other-ways-checkbox"
                        key={checkbox.label}
                        checked={
                          formState.informed_by_other_ways_checkbox.value[index]
                            ?.checked
                        }
                        onChange={() =>
                          dispatch(
                            changeInformedByOtherWaysCheckbox(checkbox.value)
                          )
                        }
                        label={checkbox.label}
                      />
                    )
                  )}
                </StyledCheckboxGroup>
              </StyledOtherInformGroup>

              <StyledUploadDocumentsGroup isMobile={isMobile}>
                <StyledUploadDocumentsTtile isMobile={isMobile}>
                  {page_content?.upload_documents_label}
                </StyledUploadDocumentsTtile>
                {maximumFileSize?.length &&
                  maximumFileSize.map((item) => (
                    <StyledUploadDocumentsParagraph
                      isMobile={isMobile}
                      key={item}
                    >
                      {item}
                    </StyledUploadDocumentsParagraph>
                  ))}
                <StyledUploadDocumentsParagraph isMobile={isMobile}>
                  {page_content?.supported_file_extensions.message}{" "}
                  {page_content?.supported_file_extensions?.list_of_text_fields?.map(
                    (file) => (
                      <StyledUploadDocumentsParagraph
                        key={file.item_text}
                        isMobile={isMobile}
                      >
                        {file.item_text}
                      </StyledUploadDocumentsParagraph>
                    )
                  )}
                </StyledUploadDocumentsParagraph>
                {(countrySelected === "SE" || countrySelected === "NL") && (
                  <StyledUploadDocumentsSpecificCheckboxContainer>
                    <StyledUploadDocumentsSpecificText>
                      {page_content?.specific_for_country?.text}
                    </StyledUploadDocumentsSpecificText>
                  </StyledUploadDocumentsSpecificCheckboxContainer>
                )}
                {countrySelected === "PT" && (
                  <StyledUploadDocumentsSpecificCheckboxContainer>
                    <StyledSpecificForCountryRadioGroup isMobile={isMobile}>
                      <StyledSpecificForCountryRadioLabel isMobile={isMobile}>
                        {page_content?.specific_for_country?.text}
                        <StyledRequired>*</StyledRequired>
                      </StyledSpecificForCountryRadioLabel>
                      <StyledRadioButtonGroup
                        data-cy="add-contact-specific-for-country-radio-button-group"
                        onChange={(event) => {
                          dispatch(changeSpecificCheckbox(event.target.value));
                        }}
                        name="specific_for_country"
                        value={formState.specific_checkbox.value}
                      >
                        <NewRadioGroup.Button
                          data-cy="add-contact-specific-for-country-radio-yes"
                          value={
                            page_content?.specific_for_country?.checkbox
                              ?.options[0]?.value
                          }
                          label={
                            page_content?.specific_for_country?.checkbox
                              ?.options[0]?.label
                          }
                        />
                        <NewRadioGroup.Button
                          data-cy="add-contact-specific-for-country-radio-no"
                          value={
                            page_content?.specific_for_country?.checkbox
                              ?.options[1]?.value
                          }
                          label={
                            page_content?.specific_for_country?.checkbox
                              ?.options[1]?.label
                          }
                        />
                      </StyledRadioButtonGroup>
                    </StyledSpecificForCountryRadioGroup>
                  </StyledUploadDocumentsSpecificCheckboxContainer>
                )}
                {page_content?.documents_list?.map((document, index) => {
                  if (document?.required_user_consent) {
                    if (formState.specific_checkbox.value === "yes") {
                      return (
                        <StyledUploadGroup
                          key={document.title}
                          isMobile={isMobile}
                        >
                          <StyledUploadDocumentsLabel>
                            {document.title}
                            {document.required && (
                              <StyledRequired>*</StyledRequired>
                            )}
                          </StyledUploadDocumentsLabel>

                          {document.information && (
                            <StyledUploadDocumentsInfo>
                              <StyledUploadDocumentsInfoText>
                                {document.information}
                              </StyledUploadDocumentsInfoText>
                            </StyledUploadDocumentsInfo>
                          )}
                          <StyledUploadButtonContainer isMobile={isMobile}>
                            {[...Array(document.total_amount)].map((_, i) => (
                              <StyledUploadButtonsGroup
                                isMobile={isMobile}
                                key={`${document.title}${fileCount[index][i]}`}
                              >
                                <StyledUploadButton
                                  data-cy="add-contact-upload-button"
                                  onClick={() =>
                                    handleClick(
                                      refs.current[fileCount[index][i]],
                                      formState.uploaded_documents.value[
                                        fileCount[index][i]
                                      ]?.isPicked,
                                      fileCount[index][i]
                                    )
                                  }
                                  text={
                                    formState.uploaded_documents.value[
                                      fileCount[index][i]
                                    ]?.filename
                                      ? `${
                                          formState.uploaded_documents.value[
                                            fileCount[index][i]
                                          ]?.filename.length > 15
                                            ? `${formState.uploaded_documents.value[
                                                fileCount[index][i]
                                              ]?.filename.slice(0, 15)}...`
                                            : formState.uploaded_documents
                                                .value[fileCount[index][i]]
                                                ?.filename
                                        }`
                                      : page_content?.upload_button_text
                                  }
                                  icon={
                                    formState.uploaded_documents.value[
                                      fileCount[index][i]
                                    ]?.isPicked
                                      ? "delete"
                                      : "plus"
                                  }
                                  variant={
                                    formState.uploaded_documents.value[
                                      fileCount[index][i]
                                    ]?.isPicked
                                      ? "primary"
                                      : "secondary"
                                  }
                                />
                                <input
                                  data-cy="add-contact-upload-input"
                                  type="file"
                                  accept={acceptedFilesType.join(", ")}
                                  // eslint-disable-next-line no-return-assign
                                  ref={(el) =>
                                    (refs.current[fileCount[index][i]] = el)
                                  }
                                  onChange={(event) =>
                                    handleChange(
                                      event,
                                      document,
                                      fileCount[index][i]
                                    )
                                  }
                                  onClick={(event) => {
                                    event.target.value = null;
                                  }}
                                  hidden
                                />
                              </StyledUploadButtonsGroup>
                            ))}
                          </StyledUploadButtonContainer>
                        </StyledUploadGroup>
                      );
                    }
                    return null;
                  }
                  return (
                    <StyledUploadGroup key={document.title} isMobile={isMobile}>
                      <StyledUploadDocumentsLabel>
                        {document.title}
                        {document.required && (
                          <StyledRequired>*</StyledRequired>
                        )}
                      </StyledUploadDocumentsLabel>

                      {document.information && (
                        <StyledUploadDocumentsInfo>
                          <StyledUploadDocumentsInfoText>
                            {document.information}
                          </StyledUploadDocumentsInfoText>
                        </StyledUploadDocumentsInfo>
                      )}
                      <StyledUploadButtonContainer isMobile={isMobile}>
                        {[...Array(document.total_amount)].map((_, i) => (
                          <StyledUploadButtonsGroup
                            isMobile={isMobile}
                            key={`${document.title}${fileCount[index][i]}`}
                          >
                            <StyledUploadButton
                              data-cy="add-contact-upload-button"
                              onClick={() =>
                                handleClick(
                                  refs.current[fileCount[index][i]],
                                  formState.uploaded_documents.value[
                                    fileCount[index][i]
                                  ]?.isPicked,
                                  fileCount[index][i]
                                )
                              }
                              text={
                                formState.uploaded_documents.value[
                                  fileCount[index][i]
                                ]?.filename
                                  ? `${
                                      formState.uploaded_documents.value[
                                        fileCount[index][i]
                                      ]?.filename.length > 15
                                        ? `${formState.uploaded_documents.value[
                                            fileCount[index][i]
                                          ]?.filename.slice(0, 15)}...`
                                        : formState.uploaded_documents.value[
                                            fileCount[index][i]
                                          ]?.filename
                                    }`
                                  : page_content?.upload_button_text
                              }
                              icon={
                                formState.uploaded_documents.value[
                                  fileCount[index][i]
                                ]?.isPicked
                                  ? "delete"
                                  : "plus"
                              }
                              variant={
                                formState.uploaded_documents.value[
                                  fileCount[index][i]
                                ]?.isPicked
                                  ? "primary"
                                  : "secondary"
                              }
                            />
                            <input
                              data-cy="add-contact-upload-input"
                              type="file"
                              accept={acceptedFilesType.join(", ")}
                              // eslint-disable-next-line no-return-assign
                              ref={(el) =>
                                (refs.current[fileCount[index][i]] = el)
                              }
                              onChange={(event) =>
                                handleChange(
                                  event,
                                  document,
                                  fileCount[index][i]
                                )
                              }
                              onClick={(event) => {
                                event.target.value = null;
                              }}
                              hidden
                            />
                          </StyledUploadButtonsGroup>
                        ))}
                      </StyledUploadButtonContainer>
                    </StyledUploadGroup>
                  );
                })}
                <StyledTCGroup isMobile={isMobile}>
                  <StyledTCCheckbox
                    data-cy="add-contact-tc-checkbox"
                    checked={formState.accept_conditions.value}
                    onChange={(checked) =>
                      dispatch(
                        changeTcAndPrivacy(checked.currentTarget.checked)
                      )
                    }
                  />
                  <StyledTCLabel>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                    >
                      {markdown}
                    </ReactMarkdown>
                  </StyledTCLabel>
                </StyledTCGroup>
              </StyledUploadDocumentsGroup>
            </StyledSection>
          </StyledContent>

          <Button
            backButtonText={page_content?.back_button?.label}
            backLink="#"
            backButtonDisabled={page_content?.back_button?.disable}
            continueButtonText={page_content?.submit_button?.label}
            onNextClick={submitForm}
            nextButtonDisabled={continueButtonDisabled}
            onBackClick={handleGoBack}
            isMobile={vp.lessThan.md}
          />
        </StyledAddContactPerson>
      )}
    </OverlayProvider>
  );
};

export default AddContactPerson;
